// components/stock/StockAlerts.tsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import PurchaseOrderForm from '../../purchase-orders/components/PurchaseOrderForm';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';

interface AlertItem {
  id: string | number;
  productSku: string;
  productName: string;
  category: string;
  currentStock: number;
  stockSecurite: number;
  stockMin: number;
  stockMax: number;
  prixUnitaire: number;
  alertType: 'Critique' | 'Bas' | 'Surstock';
  priority: 'Critique' | 'Haute' | 'Normale';
  location: string;
  lastMovement: string;
  suggestedOrder?: number;
  fournisseurId?: number;
  fournisseurName?: string;
}

export default function StockAlerts() {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [alertTypeFilter, setAlertTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  // Modales
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<AlertItem | null>(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await getResources('produits/alertes-stock');
        const produits = response.data.data || response.data;
        console.log('Produits reçus pour alertes stock :', produits);
        const processed: AlertItem[] = produits.map((p: any) => {
          const stock = p.stock_total ?? 0;
          const securite = p.stock_securite ?? p.stock_min ?? 0;
          const min = p.stock_min ?? 0;
          const max = p.stock_max ?? 0;

          let alertType: AlertItem['alertType'] = 'Bas';
          let priority: AlertItem['priority'] = 'Haute';
          let suggestedOrder: number | undefined;

          if (stock <= securite) {
            alertType = 'Critique';
            priority = 'Critique';
            suggestedOrder = Math.max((min - stock) * 2, 50);
          } else if (stock <= min) {
            alertType = 'Bas';
            priority = 'Haute';
            suggestedOrder = Math.ceil((min - stock) * 1.5);
          } else if (stock > max && max > 0) {
            alertType = 'Surstock';
            priority = 'Normale';
          } else {
            return null; // On n'ajoute pas les produits normaux
          }

          const emplacement = p.emplacements?.find((e: any) => e.pivot?.quantite > 0);
          const location = emplacement ? `${emplacement.code || emplacement.nom}` : 'Non défini';

          return {
            id: p.id,
            productSku: p.reference || p.code_barre || 'N/A',
            productName: p.designation,
            category: p.categorie?.nom || 'Non classé',
            currentStock: stock,
            stockSecurite: securite,
            stockMin: min,
            stockMax: max,
            alertType,
            prixUnitaire: p.prix_achat_ht,
            priority,
            location,
            lastMovement: p.last_movement_at || p.updated_at,
            suggestedOrder,
            fournisseurId: p.fournisseur_principal_id,
            fournisseurName: p.fournisseur_principal?.raison_sociale || 'Non défini'
          };
        }).filter(Boolean) as AlertItem[];

        setAlerts(processed);
      } catch (err) {
        console.error(err);
        toast.error("Erreur de chargement des alertes stock");
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch =
      alert.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.productSku.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = alertTypeFilter === 'all' || alert.alertType === alertTypeFilter;
    const matchesPriority = priorityFilter === 'all' || alert.priority === priorityFilter;

    return matchesSearch && matchesType && matchesPriority;
  });

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'Critique':   return 'bg-red-100 text-red-800 border-red-300';
      case 'Bas':        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Surstock':   return 'bg-blue-100 text-blue-800 border-blue-300';
      default:           return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critique':   return 'bg-red-600';
      case 'Haute':      return 'bg-orange-600';
      default:           return 'bg-gray-600';
    }
  };

  const counts = {
    critique: alerts.filter(a => a.alertType === 'Critique').length,
    bas: alerts.filter(a => a.alertType === 'Bas').length,
    surstock: alerts.filter(a => a.alertType === 'Surstock').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <span className="ml-4 text-lg text-gray-600">Analyse du stock en cours...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cartes de stats */}
      {!showOrderModal && (
      <>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Stock Critique</p>
                <p className="text-4xl font-bold mt-2">{counts.critique}</p>
                <p className="text-red-100 text-sm mt-1">Acheter immédiatement</p>
              </div>
              <i className="ri-alarm-warning-line text-4xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Stock Bas</p>
                <p className="text-4xl font-bold mt-2">{counts.bas}</p>
                <p className="text-orange-100 text-sm mt-1">Bientôt commander</p>
              </div>
              <i className="ri-time-line text-4xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Surstock</p>
                <p className="text-4xl font-bold mt-2">{counts.surstock}</p>
                <p className="text-blue-100 text-sm mt-1">Arrêter d’acheter</p>
              </div>
              <i className="ri-boxing-line text-4xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Total Alertes</p>
                <p className="text-4xl font-bold mt-2">{alerts.length}</p>
                <p className="text-green-100 text-sm mt-1">produits à surveiller</p>
              </div>
              <i className="ri-notification-line text-4xl opacity-80"></i>
            </div>
          </div>
        </div>

        {/* Filtres + Liste */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Rechercher par produit ou SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={alertTypeFilter}
              onChange={(e) => setAlertTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8"
            >
              <option value="all">Tous les types</option>
              <option value="Critique">Stock critique</option>
              <option value="Bas">Stock bas</option>
              <option value="Surstock">Surstock</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer pr-8"
            >
              <option value="all">Toutes priorités</option>
              <option value="Critique">Critique</option>
              <option value="Haute">Haute</option>
            </select>
          </div>

          <div className="space-y-4">
            {filteredAlerts.length === 0 ? (
              <div className="text-center py-16">
                <i className="ri-shield-check-line text-8xl text-green-300 mb-6"></i>
                <p className="text-2xl font-bold text-gray-800">Tout est sous contrôle !</p>
                <p className="text-gray-500 mt-2">Aucun produit en alerte de stock</p>
              </div>
            ) : (
              filteredAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`border-2 rounded-xl p-6 ${getAlertColor(alert.alertType)} shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-4 h-4 rounded-full ${getPriorityColor(alert.priority)} animate-pulse`}></div>
                        <span className="px-4 py-2 bg-white/80 rounded-full text-sm font-bold backdrop-blur">
                          {alert.alertType}
                        </span>
                        <span className="text-sm font-medium text-gray-700">
                          Priorité {alert.priority}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{alert.productName}</h3>
                          <p className="text-sm text-gray-600 font-mono mt-1">SKU: {alert.productSku}</p>
                          <p className="text-sm text-gray-600 mt-2">
                            <i className="ri-folder-line mr-2"></i>
                            {alert.category}
                          </p>
                          <p className="text-sm text-gray-600 mt-2">
                            <i className="ri-building-line mr-2"></i>
                            {alert.fournisseurName}
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Stock actuel</span>
                            <span className="text-2xl font-bold text-gray-900">{alert.currentStock}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Stock sécurité</span>
                            <span className="font-mono font-bold">{alert.stockSecurite}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Stock min / max</span>
                            <span className="font-mono">{alert.stockMin} → {alert.stockMax}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 flex flex-wrap gap-6 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <i className="ri-map-pin-line"></i>
                          <span>{alert.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-calendar-line"></i>
                          <span>{new Date(alert.lastMovement).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>

                      {alert.suggestedOrder && (
                        <div className="mt-5 p-4 bg-white/90 rounded-xl border-2 border-dashed border-blue-300">
                          <p className="text-lg font-bold text-blue-700">
                            Quantité suggérée : <span className="text-3xl">{alert.suggestedOrder}</span> unités
                          </p>
                        </div>
                      )}
                    </div>

                    {/* === Boutons d'action — "Commander" masqué si Surstock === */}
                    <div className="flex flex-col gap-3 ml-6">
                      {/* Bouton COMMANDER → visible seulement si pas Surstock */}
                      {alert.alertType !== 'Surstock' && (
                        <button
                          onClick={() => {
                            setSelectedAlert(alert);
                            setShowOrderModal(true);
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 text-lg"
                        >
                          <i className="ri-shopping-cart-line text-xl"></i>
                          Commander
                        </button>
                      )}

                      {/* Si Surstock → on affiche un message clair à la place */}
                      {alert.alertType === 'Surstock' && (
                        <div className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl font-bold text-center shadow-lg">
                          <i className="ri-forbid-line text-xl mr-2"></i>
                          Surstock – Pas de commande
                        </div>
                      )}

                      {/* Bouton DÉTAIL → toujours visible */}
                      <button
                        onClick={() => {
                          setSelectedAlert(alert);
                          setShowDetailModal(true);
                        }}
                        className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                      >
                        <i className="ri-eye-line"></i>
                        Voir détail
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </>
      )}
      {/* MODALE COMMANDE */}
      {showOrderModal && selectedAlert && (
        <PurchaseOrderForm
              order={null}
              onClose={() => {
                setShowOrderModal(false);
                setSelectedAlert(null);
                toast.success("Bon de commande créé avec succès !");
              }}
              initialProduct={{
                id: selectedAlert.id,
                name: selectedAlert.productName,
                suggestedQuantity: selectedAlert.suggestedOrder || 50,
                fournisseurId: selectedAlert.fournisseurId,
                prixUnitaire: selectedAlert.prixUnitaire,
                stockMax: selectedAlert.stockMax,
              }}
            />
      )}

      {/* MODALE DÉTAIL PRODUIT */}
      {showDetailModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedAlert.productName}</h3>
                  <p className="text-sm text-gray-600 font-mono mt-1">SKU: {selectedAlert.productSku}</p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-50 p-5 rounded-xl">
                  <p className="text-sm text-gray-600">Catégorie</p>
                  <p className="text-lg font-bold text-gray-900">{selectedAlert.category}</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-xl">
                  <p className="text-sm text-gray-600">Fournisseur</p>
                  <p className="text-lg font-bold text-gray-900">{selectedAlert.fournisseurName}</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-xl border-2 border-red-200">
                <h4 className="font-bold text-lg text-red-800 mb-4">Situation Stock</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Stock actuel</p>
                    <p className="text-3xl font-bold text-red-600">{selectedAlert.currentStock}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Stock sécurité</p>
                    <p className="text-2xl font-bold text-orange-600">{selectedAlert.stockSecurite}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Stock min</p>
                    <p className="text-2xl font-bold text-gray-700">{selectedAlert.stockMin}</p>
                  </div>
                </div>
              </div>

              {selectedAlert.alertType !== 'Surstock' ? (
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowOrderModal(true);
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-3"
                  >
                    <i className="ri-shopping-cart-line text-xl"></i>
                    Commander ce produit
                  </button>
                </div>
              ) : (
                <div className="flex justify-end">
                  <div className="px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-xl font-bold text-lg shadow-lg flex items-center gap-3">
                    <i className="ri-forbid-line text-xl"></i>
                    Surstock – Aucune commande nécessaire
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}